// src/core/platform/platformBridge.ts
// 크로스 플랫폼 추상화 인터페이스

/** 파일 저장 다이얼로그 옵션 */
export interface SaveDialogOptions {
  title?: string
  defaultPath?: string
  buttonLabel?: string
  filters?: Array<{ name: string; extensions: string[] }>
}

/** 파일 저장 다이얼로그 결과 */
export interface SaveDialogResult {
  canceled: boolean
  filePath?: string
}

/** 플랫폼별 지원 가능 기능 */
export type PlatformFeature =
  | "npki-cert" // NPKI 인증서 관리
  | "hira-iea" // HIRA IEA 연동
  | "cert-agent" // 인증서 에이전트
  | "raw-print" // RAW 영수증 인쇄
  | "network-scan" // 네트워크 스캔
  | "screen-capture" // 화면 캡처
  | "chat-window" // 별도 채팅 윈도우
  | "window-control" // 윈도우 제어 (최소화/최대화)
  | "file-dialog" // 네이티브 파일 다이얼로그
  | "background-image" // 배경 이미지 관리
  | "edi-file" // EDI 파일 처리
  | "remote-support" // 원격 지원
  | "zoom-control" // 줌 인/아웃 제어
  | "image-viewer" // 이미지 뷰어 별도 창

/** 플랫폼 타입 */
export type PlatformType = "electron" | "capacitor" | "web"

/** 플랫폼 브릿지 인터페이스 */
export interface PlatformBridge {
  readonly platform: PlatformType

  // ── 기능 체크 ──
  isFeatureAvailable(feature: PlatformFeature): boolean

  // ── 파일 관련 ──
  showSaveDialog(options?: SaveDialogOptions): Promise<SaveDialogResult>
  writeFile(path: string, content: string): Promise<{ success: boolean; error?: string }>
  writeBinaryFile(path: string, data: Uint8Array | ArrayBuffer): Promise<{ success: boolean; error?: string }>
  openFileLocation(filePath: string): Promise<{ success: boolean; error?: string }>

  // ── 앱 제어 ──
  openExternalUrl(url: string): Promise<void>
  restartApp(): Promise<void>
  exitApp(): Promise<void>

  // ── 서버 검색 ──
  scanNetwork(): Promise<Array<string | { ip: string; port?: number; protocol?: string }>>
  checkServer(ip: string, port: number): Promise<boolean>
  diagnoseTlsCertificate?(
    ip: string,
    port: number
  ): Promise<{
    certificatePresent: boolean
    ipCovered: boolean
    error?: string
  }>
  getLocalIp(): Promise<string | null>
  saveProxyConfig(config: unknown): Promise<void>
}

/** 런타임 플랫폼 감지 */
export function detectPlatform(): PlatformType {
  if (typeof window !== "undefined") {
    if (window.electronAPI) return "electron"
    // Capacitor 전역 객체 확인
    const cap = (window as unknown as Record<string, unknown>).Capacitor as { isNativePlatform?: () => boolean } | undefined
    if (cap?.isNativePlatform?.()) return "capacitor"
  }
  return "web"
}

